import { useState, useEffect } from 'react';

/**
 * Hook that debounces a value by delaying updates until after the specified delay.
 *
 * @param value - The value to debounce
 * @param delay - The debounce delay in milliseconds
 * @returns The debounced value
 *
 * @example
 * const [searchQuery, setSearchQuery] = useState('');
 * const debouncedSearchQuery = useDebounce(searchQuery, 300);
 *
 * // Use debouncedSearchQuery for API calls
 * useEffect(() => {
 *   if (debouncedSearchQuery) {
 *     performSearch(debouncedSearchQuery);
 *   }
 * }, [debouncedSearchQuery]);
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
